import { zodResolver } from '@hookform/resolvers/zod';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { usePasswordChange } from '../hooks/usePasswordChange';
import { passwordSchema } from '../validations/changePassword';
import { Eye, EyeOff, Loader, Lock } from 'lucide-react';

const PasswordChangeForm = () => {
  const { mutate: changePassword, isPending } = usePasswordChange();
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
    mode: 'onChange',
  });

  const onSubmit = (data) => {
    changePassword(data, {
      onSuccess: () => {
        reset();
      },
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

    if (strength <= 2) return { strength: 33, label: 'Weak', color: 'bg-red-500' };
    if (strength <= 4) return { strength: 66, label: 'Medium', color: 'bg-yellow-500' };
    return { strength: 100, label: 'Strong', color: 'bg-green-500' };
  };

  return (
    <div className='bg-card border border-border rounded-lg p-4 sm:p-6'>
      <div className='mb-6'>
        <h3 className='text-lg font-semibold text-foreground mb-1'>Change Password</h3>
        <p className='text-xs sm:text-sm text-muted-foreground'>
          Ensure your account is using a long, random password to stay secure
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className='space-y-4 sm:space-y-6'>
        {/* Current Password */}
        <div>
          <label className='block text-sm font-medium text-foreground mb-2'>
            Current Password <span className='text-red-500'>*</span>
          </label>
          <div className='relative'>
            <Controller
              name='currentPassword'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type={showPasswords.current ? 'text' : 'password'}
                  placeholder='Enter current password'
                  className={`pr-10 ${errors.currentPassword ? 'border-red-500' : ''}`}
                  autoComplete='current-password'
                  autoCorrect='off'
                  autoCapitalize='none'
                  spellCheck={false}
                />
              )}
            />
            <button
              type='button'
              onClick={() => togglePasswordVisibility('current')}
              className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground'
            >
              {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.currentPassword && <p className='text-xs text-red-500 mt-1'>{errors.currentPassword.message}</p>}
        </div>

        {/* New Password */}
        <div>
          <label className='block text-sm font-medium text-foreground mb-2'>
            New Password <span className='text-red-500'>*</span>
          </label>
          <div className='relative'>
            <Controller
              name='newPassword'
              control={control}
              render={({ field }) => {
                const strength = getPasswordStrength(field.value);
                return (
                  <>
                    <Input
                      {...field}
                      type={showPasswords.new ? 'text' : 'password'}
                      placeholder='Enter new password'
                      className={`pr-10 ${errors.newPassword ? 'border-red-500' : ''}`}
                      autoComplete='new-password'
                      autoCorrect='off'
                      autoCapitalize='none'
                      spellCheck={false}
                    />
                    {field.value && (
                      <div className='mt-2'>
                        <div className='flex items-center justify-between mb-1'>
                          <span className='text-xs text-muted-foreground'>Password strength</span>
                          <span
                            className={`text-xs font-medium ${
                              strength.label === 'Weak'
                                ? 'text-red-500'
                                : strength.label === 'Medium'
                                ? 'text-yellow-500'
                                : 'text-green-500'
                            }`}
                          >
                            {strength.label}
                          </span>
                        </div>
                        <div className='w-full bg-muted rounded-full h-1.5'>
                          <div
                            className={`h-1.5 rounded-full transition-all duration-300 ${strength.color}`}
                            style={{ width: `${strength.strength}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </>
                );
              }}
            />
            <button
              type='button'
              onClick={() => togglePasswordVisibility('new')}
              className='absolute right-3 top-3 text-muted-foreground hover:text-foreground'
            >
              {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.newPassword && <p className='text-xs text-red-500 mt-1'>{errors.newPassword.message}</p>}
          <div className='mt-2 space-y-1'>
            <p className='text-xs text-muted-foreground'>Password must contain:</p>
            <ul className='text-xs text-muted-foreground space-y-0.5 pl-4'>
              <li>• At least 8 characters</li>
              <li>• At least one uppercase and lowercase letter</li>
              <li>• At least one number and special character</li>
            </ul>
          </div>
        </div>

        {/* Confirm New Password */}
        <div>
          <label className='block text-sm font-medium text-foreground mb-2'>
            Confirm New Password <span className='text-red-500'>*</span>
          </label>
          <div className='relative'>
            <Controller
              name='confirmNewPassword'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type={showPasswords.confirm ? 'text' : 'password'}
                  placeholder='Confirm new password'
                  className={`pr-10 ${errors.confirmNewPassword ? 'border-red-500' : ''}`}
                  autoComplete='new-password'
                  autoCorrect='off'
                  autoCapitalize='none'
                  spellCheck={false}
                />
              )}
            />
            <button
              type='button'
              onClick={() => togglePasswordVisibility('confirm')}
              className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground'
            >
              {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.confirmNewPassword && (
            <p className='text-xs text-red-500 mt-1'>{errors.confirmNewPassword.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <div className='flex flex-col sm:flex-row gap-3 pt-4'>
          <Button
            type='submit'
            disabled={!isDirty || isSubmitting || isPending}
            className='flex items-center justify-center space-x-2 w-full sm:w-auto'
          >
            {isSubmitting || isPending ? (
              <>
                <Loader size={16} className='animate-spin' />
                <span>Updating...</span>
              </>
            ) : (
              <>
                <Lock size={16} />
                <span>Update Password</span>
              </>
            )}
          </Button>

          {isDirty && (
            <Button
              type='button'
              variant='outline'
              onClick={() => reset()}
              disabled={isSubmitting || isPending}
              className='w-full sm:w-auto'
            >
              Cancel
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default PasswordChangeForm;
